import { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styled from "styled-components";
import IP_URL from "../connect";
import Navbar from "../Navbar";
import Sidebar from "../Sidebar";
import { useSelector } from "react-redux";
import { selectUser } from "../../Redux/currentUserSlice";

// Styled Components
const Main = styled.div`
  background: #f9fafb;
  min-height: 100vh;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  padding: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 12px;
  }
`;

const Card = styled.div`
  flex: 1;

  width:90%;
  background: white;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  margin: auto;

  &:hover {
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.1);
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  margin-top: 4px;
  font-size: 0.95rem;

  &:focus {
    border-color: #2563eb;
    outline: none;
  }
`;

const Select = styled.select`
  padding: 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  width: 100%;
  margin-top: 4px;
  background: #fff;
  font-size: 0.95rem;

  &:focus {
    border-color: #2563eb;
    outline: none;
  }
`;

const Button = styled.button`
  background: ${({ disabled }) => (disabled ? "#93c5fd" : "#2563eb")};
  color: white;
  padding: 12px;
  border: none;
  border-radius: 8px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  font-weight: 600;
  margin-top: 1rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ disabled }) => (disabled ? "#93c5fd" : "#1d4ed8")};
  }
`;

const SelectItem = styled.option``;

const CommandGenerator = () => {
  const [imeis, setImeis] = useState([]);
  const [command, setCommand] = useState("");
  const [params, setParams] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { token } = useSelector(selectUser);

  // Toggle sidebar
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Handle IMEI input
  const handleImeisChange = (e) => {
    const imeiArray = e.target.value
      .split(",")
      .map((val) => val.trim())
      .filter((val) => /^\d+$/.test(val))
      .map(Number);
    setImeis(imeiArray);
  };

  // Validate params
  const areParamsValid = () => {
    switch (command) {
      case "setMaximumReading":
        return params.maxReading;
      case "reportingCycleInterval":
        return params.interval;
      case "changeSerial":
        return params.serial?.trim();
      case "changeIP":
        return params.ip?.trim();
      case "setPN":
        return params.pn !== undefined;
      case "setReportingRange":
        return params.reportingRange !== undefined;
      case "valveControl":
        return params.valveControl !== undefined;
      case "readDenseData":
        return params.date;
      case "deviceCalibrationNodeReading":
        return params.calibration;
      case "setAvailableWaterBalance":
        return params.balance;
      case "time_calibration":
        return true;
      default:
        return false;
    }
  };

  // Send command
  const handleSend = async () => {
    if (loading) return;

    if (imeis.length === 0 || !command) {
      toast.error("Please enter at least one IMEI and select a command", {
        position: "bottom-center",
      });
      return;
    }
    if (!areParamsValid()) {
      toast.error("Please fill in the required parameters", {
        position: "bottom-center",
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${IP_URL}/setCommands/add-command`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ imeis, command, params }),
      });

      if (res.ok) {
        toast.success("✅ Command queued successfully!", {
          position: "bottom-right",
          autoClose: 3000,
        });
        setParams({});
      } else {
        toast.error("❌ Failed to send command", { position: "bottom-center" });
      }
    } catch (err) {
      toast.error("⚠️ Error sending command", { position: "bottom-center" });
    } finally {
      setLoading(false);
    }
  };

  // Render dynamic params
  const renderParams = () => {
    switch (command) {
      case "setMaximumReading":
        return (
          <>
            <Label>Maximum Meter Reading</Label>
            <Input
              type="number"
              placeholder="Enter maximum reading"
              onChange={(e) =>
                setParams({ ...params, maxReading: parseInt(e.target.value) })
              }
            />
          </>
        );
      case "reportingCycleInterval":
        return (
          <>
            <Label>Reporting Interval (seconds)</Label>
            <Input
              type="number"
              placeholder="e.g. 3600 for 1 hour"
              onChange={(e) =>
                setParams({ ...params, interval: parseInt(e.target.value) })
              }
            />
          </>
        );
      case "changeSerial":
        return (
          <>
            <Label>New Serial Number</Label>
            <Input
              type="text"
              placeholder="Enter serial number"
              onChange={(e) =>
                setParams({ ...params, serial: e.target.value })
              }
            />
          </>
        );
      case "changeIP":
        return (
          <>
            <Label>New IP:Port</Label>
            <Input
              type="text"
              placeholder="e.g. 192.168.1.10:5000"
              onChange={(e) => setParams({ ...params, ip: e.target.value })}
            />
          </>
        );
      case "setPN":
        return (
          <>
            <Label>New PN Value</Label>
            <Select
              onChange={(e) =>
                setParams({ ...params, pn: parseInt(e.target.value) })
              }
            >
              <SelectItem value="">-- Select PN --</SelectItem>
              <SelectItem value="0">0 - Direct Meter Reading</SelectItem>
              <SelectItem value="1">1 - 1 L/P</SelectItem>
              <SelectItem value="2">2 - 10 L/P</SelectItem>
              <SelectItem value="3">3 - 100 L/P</SelectItem>
              <SelectItem value="4">4 - 1000 L/P</SelectItem>
              <SelectItem value="5">5 - 0.5 L/P</SelectItem>
              <SelectItem value="6">6 - 5 L/P</SelectItem>
              <SelectItem value="7">7 - 10000 L/P</SelectItem>
            </Select>
          </>
        );
      case "setReportingRange":
        return (
          <>
            <Label>Set Reporting Range</Label>
            <Select
              onChange={(e) =>
                setParams({
                  ...params,
                  reportingRange: parseInt(e.target.value),
                })
              }
            >
              <SelectItem value="">-- Select Range --</SelectItem>
              <SelectItem value="20">20 (0 - 10:00)</SelectItem>
              <SelectItem value="321">321 (3 - 13:30)</SelectItem>
              <SelectItem value="236">236 (2:00 - 20:00)</SelectItem>
            </Select>
          </>
        );
      case "valveControl":
        return (
          <>
            <Label>Valve Control</Label>
            <Select
              onChange={(e) =>
                setParams({
                  ...params,
                  valveControl: parseInt(e.target.value),
                })
              }
            >
              <SelectItem value="">-- Set Valve --</SelectItem>
              <SelectItem value="0">Open</SelectItem>
              <SelectItem value="1">Close</SelectItem>
            </Select>
          </>
        );
      case "readDenseData":
        return (
          <>
            <Label>Read Dense Data</Label>
            <Input
              type="number"
              placeholder="yymmdd (daily) or yymm (monthly)"
              onChange={(e) =>
                setParams({ ...params, date: parseInt(e.target.value.trim()) })
              }
            />
          </>
        );
      case "deviceCalibrationNodeReading":
        return (
          <>
            <Label>Device Calibration Node Reading</Label>
            <Input
              type="number"
              placeholder="Enter calibration node"
              onChange={(e) =>
                setParams({
                  ...params,
                  calibration: parseInt(e.target.value),
                })
              }
            />
          </>
        );
      case "setAvailableWaterBalance":
        return (
          <>
            <Label>Available Water Balance</Label>
            <Input
              type="number"
              placeholder="Enter balance in litres"
              onChange={(e) =>
                setParams({
                  ...params,
                  balance: parseInt(e.target.value),
                })
              }
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Main>
      <Navbar showMenuButton toggleSidebar={toggleSidebar} />
      <Container>
        <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

        <Card>
          <CardContent>
            <h2 style={{ color: "#1e40af" }}>Generate Command</h2>

            <Label>Enter IMEI(s)</Label>
            <Input
              type="text"
              placeholder="e.g. 123456789012345, 987654321098765"
              onChange={handleImeisChange}
            />
            <small style={{ color: "#6b7280" }}>
              Separate multiple IMEIs with commas
            </small>

            <Label>Select Command</Label>
            <Select
              value={command}
              onChange={(e) => {
                setCommand(e.target.value);
                setParams({});
              }}
            >
              <SelectItem value="">-- Select Command --</SelectItem>
              <SelectItem value="time_calibration">
                Manual Time Calibration
              </SelectItem>
              <SelectItem value="valveControl">Valve Control</SelectItem>
              <SelectItem value="setMaximumReading">
                Set Maximum Reading
              </SelectItem>
              <SelectItem value="reportingCycleInterval">
                Reporting Cycle Interval
              </SelectItem>
              <SelectItem value="changeSerial">Change Serial Number</SelectItem>
              <SelectItem value="changeIP">Change IP:Port</SelectItem>
              <SelectItem value="setPN">Set PN</SelectItem>
              <SelectItem value="setReportingRange">
                Set Reporting Range
              </SelectItem>
              <SelectItem value="readDenseData">Read Dense Data</SelectItem>
              <SelectItem value="deviceCalibrationNodeReading">
                Device Calibration Node Reading
              </SelectItem>
              <SelectItem value="setAvailableWaterBalance">
                Set Available Water Balance
              </SelectItem>
            </Select>

            {renderParams()}

            <Button
              onClick={handleSend}
              disabled={loading || !areParamsValid() || !command}
            >
              {loading ? "Sending..." : "Send Command"}
            </Button>
          </CardContent>
        </Card>
      </Container>

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        pauseOnHover
        theme="colored"
        toastStyle={{
          width: "90%",
          maxWidth: "400px",
          fontSize: "0.9rem",
        }}
      />
    </Main>
  );
};

export default CommandGenerator;
